import React from "react";

/**
 * ProtectedImage — renders an image with anti-theft protection:
 *  - DENSELY TILED diagonal "OBITUARY RELIEF" watermark — every crop region still carries the mark
 *  - Right-click disabled (no "Save image as…")
 *  - Drag disabled (can't drag to desktop)
 *  - user-select / pointer-events disabled
 *  - Multiple corner brand marks (so cropping any corner still shows one)
 */
export default function ProtectedImage({ src, alt, wrapStyle = {}, imgStyle = {}, watermarkText = "OBITUARY RELIEF" }) {
  const stop = (e) => { e.preventDefault(); return false; };

  // SVG-based tiled watermark — repeats every ~140px so cropping ANY portion
  // larger than ~140×80px will still contain at least one full stamp.
  const tileSvg = encodeURIComponent(`
    <svg xmlns='http://www.w3.org/2000/svg' width='220' height='140' viewBox='0 0 220 140'>
      <g transform='rotate(-28 110 70)' opacity='0.32'>
        <text x='110' y='62' text-anchor='middle' font-family='Manrope, Arial, sans-serif' font-size='14' font-weight='700' fill='white' letter-spacing='2'>${watermarkText}</text>
        <text x='110' y='86' text-anchor='middle' font-family='Manrope, Arial, sans-serif' font-size='9' fill='white' letter-spacing='1'>© obituaryrelief.com</text>
      </g>
    </svg>
  `).replace(/\s+/g, " ");

  return (
    <div
      onContextMenu={stop}
      onDragStart={stop}
      style={{
        position: "relative",
        overflow: "hidden",
        userSelect: "none",
        WebkitUserSelect: "none",
        ...wrapStyle,
      }}
      data-testid="protected-image"
    >
      <img
        src={src}
        alt={alt || ""}
        draggable={false}
        onContextMenu={stop}
        style={{
          display: "block",
          pointerEvents: "none",
          WebkitTouchCallout: "none",
          ...imgStyle,
        }}
      />
      {/* Dense tiled watermark — every crop region still carries the brand */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          backgroundImage: `url("data:image/svg+xml;utf8,${tileSvg}")`,
          backgroundRepeat: "repeat",
          backgroundSize: "220px 140px",
          mixBlendMode: "screen",
        }}
      />
      {/* Diagonal stripes adding extra texture, harder to clean-edit out */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            `repeating-linear-gradient(
              -28deg,
              transparent 0,
              transparent 40px,
              rgba(255,255,255,0.06) 40px,
              rgba(255,255,255,0.06) 80px
            )`,
        }}
      />
      {/* Brand marks in all 4 corners so cropping any corner still shows one */}
      {["top-left", "top-right", "bottom-left", "bottom-right"].map((pos) => {
        const style = {
          position: "absolute",
          pointerEvents: "none",
          color: "rgba(255,255,255,0.55)",
          fontSize: 9,
          fontFamily: "Manrope, Arial, sans-serif",
          letterSpacing: "0.06em",
          textShadow: "0 1px 2px rgba(0,0,0,0.55)",
        };
        if (pos.includes("top")) style.top = 6; else style.bottom = 4;
        if (pos.includes("left")) style.left = 6; else style.right = 6;
        return <div key={pos} aria-hidden="true" style={style}>© obituaryrelief.com</div>;
      })}
    </div>
  );
}
